import { FaPlay, FaPause } from 'react-icons/fa';
import { HiBackward, HiForward } from 'react-icons/hi2';
import { TbRewindBackward5, TbRewindBackward30, TbRewindForward30, TbRewindForward5 } from 'react-icons/tb';

interface ControlsProps {
  isPlaying: boolean;
  isBuffering?: boolean;
  isLive?: boolean;
  onPlayPause: () => void;
  onForward: () => void;
  onBackward: () => void;
  onForwardLarge?: () => void;
  onBackwardLarge?: () => void;
  onPrevTrack?: () => void;
  onNextTrack?: () => void;
  hasPrevTrack?: boolean;
  hasNextTrack?: boolean;
  disabled?: boolean;
  isTransitioning?: boolean;
  behindLive?: boolean;
  onJumpToLive?: () => void;
}

export function Controls({
  isPlaying,
  isBuffering = false,
  isLive = false,
  onPlayPause,
  onForward,
  onBackward,
  onForwardLarge,
  onBackwardLarge,
  onPrevTrack,
  onNextTrack,
  hasPrevTrack = false,
  hasNextTrack = false,
  disabled = false,
  isTransitioning = false,
  behindLive = false,
  onJumpToLive,
}: ControlsProps) {
  // Show loading spinner when transitioning or buffering while playing
  const showSpinner = isTransitioning || (isBuffering && isPlaying);

  return (
    <div className={`flex items-center justify-center gap-1 py-2
                    transition-opacity duration-300 ease-in-out
                    ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
      {/* Previous Track */}
      {onPrevTrack && (
        <button
          onClick={onPrevTrack}
          disabled={disabled || !hasPrevTrack}
          aria-label="Previous track"
          className="p-2 text-[var(--color-text-muted)]
                     hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-elevated)]
                     active:scale-95 transition-all duration-150 ease-out
                     focus:outline-none focus-visible:ring-2
                     focus-visible:ring-[var(--color-primary)] rounded-full
                     disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <HiBackward size={20} />
        </button>
      )}

      {/* Rewind 5min - smaller, secondary (hidden in live mode) */}
      {!isLive && onBackwardLarge && (
        <button
          onClick={onBackwardLarge}
          disabled={disabled}
          aria-label="Rewind 5 minutes"
          className="p-2 text-[var(--color-text-muted)]
                     hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-elevated)]
                     active:scale-95 transition-all duration-150 ease-out
                     focus:outline-none focus-visible:ring-2
                     focus-visible:ring-[var(--color-primary)] rounded-full
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <TbRewindBackward5 size={20} strokeWidth={1.5} />
          <span className="sr-only">5 min</span>
        </button>
      )}

      {/* Rewind 30s (hidden in live mode) */}
      {!isLive && (
        <button
          onClick={onBackward}
          disabled={disabled}
          aria-label="Rewind 30 seconds (J or Left Arrow)"
          tabIndex={1}
          className="p-3 text-[var(--color-text-secondary)]
                     hover:text-[var(--color-text)] hover:bg-[var(--color-surface-elevated)]
                     active:scale-95 transition-all duration-150 ease-out
                     focus:outline-none focus-visible:ring-2
                     focus-visible:ring-[var(--color-primary)] rounded-full
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <TbRewindBackward30 size={28} strokeWidth={1.5} />
        </button>
      )}

      {/* Play/Pause - larger, more prominent */}
      <button
        onClick={onPlayPause}
        disabled={disabled || isTransitioning}
        aria-label={isBuffering ? 'Buffering...' : isPlaying ? 'Pause (Space or K)' : 'Play (Space or K)'}
        tabIndex={2}
        className={`w-14 h-14 mx-2 flex items-center justify-center relative
                   text-white bg-[var(--color-primary)] rounded-full
                   shadow-lg shadow-[var(--color-primary)]/30
                   hover:shadow-xl hover:shadow-[var(--color-primary)]/40
                   hover:scale-105 active:scale-95
                   transition-all duration-200 ease-out
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                   focus-visible:ring-offset-[var(--color-bg)]
                   focus-visible:ring-[var(--color-primary)]
                   disabled:cursor-not-allowed disabled:shadow-none
                   ${showSpinner ? 'scale-95' : ''}`}
      >
        {showSpinner ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : isPlaying ? (
          <FaPause size={20} />
        ) : (
          <FaPlay size={20} className="ml-1" />
        )}

        {/* Buffering ring indicator (shows around the button when buffering but not transitioning) */}
        {isBuffering && isPlaying && !isTransitioning && (
          <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-pulse" />
        )}
      </button>

      {/* Jump to Live button - shown inline when user is behind the live edge */}
      {isLive && behindLive && onJumpToLive && (
        <button
          onClick={onJumpToLive}
          className="inline-flex items-center gap-2 px-4 py-1.5 ml-1 text-xs font-bold
                     text-white bg-red-500 rounded-full shadow-lg
                     hover:bg-red-600 hover:shadow-xl
                     active:scale-95 transition-all duration-200 ease-out
                     focus:outline-none focus-visible:ring-2
                     focus-visible:ring-red-400 focus-visible:ring-offset-2
                     focus-visible:ring-offset-[var(--color-bg)]
                     animate-fade-in"
          aria-label="Jump back to live stream"
        >
          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
          Back to live
        </button>
      )}

      {/* Forward 30s (hidden in live mode) */}
      {!isLive && (
        <button
          onClick={onForward}
          disabled={disabled}
          aria-label="Forward 30 seconds (L or Right Arrow)"
          tabIndex={3}
          className="p-3 text-[var(--color-text-secondary)]
                     hover:text-[var(--color-text)] hover:bg-[var(--color-surface-elevated)]
                     active:scale-95 transition-all duration-150 ease-out
                     focus:outline-none focus-visible:ring-2
                     focus-visible:ring-[var(--color-primary)] rounded-full
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <TbRewindForward30 size={28} strokeWidth={1.5} />
        </button>
      )}

      {/* Forward 5min - smaller, secondary (hidden in live mode) */}
      {!isLive && onForwardLarge && (
        <button
          onClick={onForwardLarge}
          disabled={disabled}
          aria-label="Forward 5 minutes"
          className="p-2 text-[var(--color-text-muted)]
                     hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-elevated)]
                     active:scale-95 transition-all duration-150 ease-out
                     focus:outline-none focus-visible:ring-2
                     focus-visible:ring-[var(--color-primary)] rounded-full
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <TbRewindForward5 size={20} strokeWidth={1.5} />
          <span className="sr-only">5 min</span>
        </button>
      )}

      {/* Next Track */}
      {onNextTrack && (
        <button
          onClick={onNextTrack}
          disabled={disabled || !hasNextTrack}
          aria-label="Next track"
          className="p-2 text-[var(--color-text-muted)]
                     hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-elevated)]
                     active:scale-95 transition-all duration-150 ease-out
                     focus:outline-none focus-visible:ring-2
                     focus-visible:ring-[var(--color-primary)] rounded-full
                     disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <HiForward size={20} />
        </button>
      )}
    </div>
  );
}
